import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  Lock,
  Mail,
  Loader2,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  ArrowLeft,
  Eye,
  EyeOff,
  KeyRound
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_isaac-platform/artifacts/9qsxumik_Sterile%20Mate%20Logo%20%282%29.jpg";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const userType = searchParams.get("type") || "admin";

  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(!!token);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenEmail, setTokenEmail] = useState("");
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    new_password: "",
    confirm_password: ""
  });

  // Validate token on mount if present
  useEffect(() => {
    if (token) {
      validateToken();
    }
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await axios.get(`${API}/password-reset/validate-token`, {
        params: { token, user_type: userType }
      });
      
      if (response.data.valid) {
        setTokenValid(true);
        setTokenEmail(response.data.email);
      } else {
        setTokenValid(false);
        toast.error(response.data.reason || "Invalid reset link");
      }
    } catch (error) {
      setTokenValid(false);
      toast.error("Failed to validate reset link");
    } finally {
      setValidating(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (formData.new_password !== formData.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }

    if (formData.new_password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/password-reset/confirm`, {
        token,
        new_password: formData.new_password,
        user_type: userType
      });
      
      setSuccess(true);
      toast.success("Password reset successfully!");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to reset password";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const getLoginPath = () => {
    switch (userType) {
      case "hospital": return "/hospital/login";
      case "contractor": return "/contractor/login";
      case "supplier": return "/supplier/login";
      default: return "/login";
    }
  };

  const getUserTypeLabel = () => {
    switch (userType) {
      case "hospital": return "Hospital Staff";
      case "contractor": return "Contractor";
      case "supplier": return "Supplier";
      default: return "Staff";
    }
  };

  // Loading state while validating token
  if (validating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-cyan-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Validating reset link...</p>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (token && !tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/">
              <img src={LOGO_URL} alt="Ask Isaac" className="h-14 mx-auto mb-4" />
            </Link>
          </div>
          
          <div className="bg-slate-800/50 border border-red-500/30 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Invalid Reset Link</h2>
            <p className="text-slate-400 mb-6">
              This password reset link is invalid or has expired. 
              Please request a new password reset.
            </p>
            <Button 
              onClick={() => navigate(getLoginPath())}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/">
              <img src={LOGO_URL} alt="Ask Isaac" className="h-14 mx-auto mb-4" />
            </Link>
          </div>
          
          <div className="bg-slate-800/50 border border-emerald-500/30 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Password Reset Complete!</h2>
            <p className="text-slate-400 mb-6">
              Your password has been successfully updated. 
              You can now log in with your new password.
            </p>
            <Button 
              onClick={() => navigate(getLoginPath())}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Go to Login
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Password reset form (with valid token)
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_URL} alt="Ask Isaac" className="h-14 mx-auto mb-4" />
          </Link>
          <h1 className="text-2xl font-bold text-white">Reset Your Password</h1>
          <p className="text-slate-400 mt-2">
            {tokenEmail && (
              <span className="text-cyan-400">{tokenEmail}</span>
            )}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="bg-cyan-50 border-b border-cyan-100 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                <KeyRound className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Create New Password</p>
                <p className="text-sm text-slate-500">{getUserTypeLabel()} Account</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleResetPassword} className="p-6 space-y-5">
            <div>
              <Label className="flex items-center gap-1">
                <Lock className="w-4 h-4 text-slate-400" />
                New Password
              </Label>
              <div className="relative mt-1">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={formData.new_password}
                  onChange={(e) => setFormData({...formData, new_password: e.target.value})}
                  placeholder="Minimum 8 characters"
                  className="pr-10"
                  required
                  minLength={8}
                  data-testid="reset-new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label className="flex items-center gap-1">
                <Lock className="w-4 h-4 text-slate-400" />
                Confirm Password
              </Label>
              <div className="relative mt-1">
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirm_password}
                  onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
                  placeholder="Re-enter your password"
                  className="pr-10"
                  required
                  data-testid="reset-confirm-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-amber-800 text-sm">
                <strong>Password requirements:</strong>
                <br />• Minimum 8 characters
              </p>
            </div>

            <Button 
              type="submit"
              disabled={loading}
              className="w-full bg-cyan-600 hover:bg-cyan-700"
              data-testid="reset-submit"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Resetting...</>
              ) : (
                <>Reset Password <ArrowRight className="w-4 h-4 ml-2" /></>
              )}
            </Button>
          </form>

          <div className="px-6 pb-6 pt-2 border-t border-slate-100">
            <Link
              to={getLoginPath()}
              className="w-full text-center text-sm text-slate-500 hover:text-cyan-600 block"
            >
              <ArrowLeft className="w-4 h-4 inline mr-1" />
              Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
